import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, authApi } from '../lib/api.ts'
import type { User } from '../lib/types.ts'

export const SESSION_KEY = ['session'] as const

/**
 * Current user, or null when not logged in. A 401 is a valid answer here, not a failure, so it
 * resolves to null instead of throwing — otherwise React Query would retry the login screen.
 */
export function useSession() {
  return useQuery<User | null>({
    queryKey: SESSION_KEY,
    queryFn: async () => {
      try {
        const { user } = await authApi.me()
        return user
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null
        throw err
      }
    },
    retry: false,
    staleTime: 60_000,
  })
}

export function useLogin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      authApi.login(email, password),
    onSuccess: ({ user }) => {
      queryClient.setQueryData(SESSION_KEY, user)
    },
  })
}

export function useLogout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => authApi.logout(),
    onSuccess: () => {
      queryClient.setQueryData(SESSION_KEY, null)
      // Nothing cached belongs to a logged-out user.
      void queryClient.clear()
    },
  })
}
